import {FIELD_BIOME_LAYER_KEYS, FIELD_BIOME_TEXTURES} from '../../../configs/fieldBiomeEffects';
import {
  TWO_PI,
  expandBox,
  hash01,
  intersectBoxes,
  lerp,
  randomIn,
  randomSign,
  resolveLifeEnvelope,
  smoothstep,
  toRadians,
  writeHiddenPose,
  writePose,
} from './biomeMath';

/**
 * ВЬЮГА: снежный заряд над зимним полем (часть эффекта зимних подложек, см.
 * ./snowDrift и configs/biomeEffects/snowDrift). Чистые функции без Phaser и
 * DOM: по этому описанию частицы каждый кадр ставит сцена
 * (scenes/FieldBiomeEffects).
 *
 * Заряд — одна группа частиц на полминуты: плавно налетает, держится и плавно
 * стихает (общая огибающая — resolveSnowSquallStrength; по ней же нарастает и
 * затихает вой вьюги, см. scenes/FieldAmbience), а пока держится — идет
 * волнами. В группе:
 *  - пелена: одно большое мягкое пятно на всю область заряда — ровно
 *    выбеливает поле, пока идет снег;
 *  - мгла: большие мягкие клубы плывут по ветру матча (тому же, что несет
 *    облака) — местами поле затягивает плотнее;
 *  - снег: рои снежинок — кадры с россыпью мелких округлых хлопьев. Рой
 *    летит через область заряда по ветру, покачиваясь, уходит за ее край и
 *    появляется с наветренной стороны снова: путь замкнут по кругу, поэтому
 *    поза остается функцией времени;
 *  - завихрения: отдельные снежинки, по одной на спрайт, — их несет струями
 *    и закручивает небольшими вихрями.
 *
 * РОЙ ЛЕТИТ ПРЯМО И НЕ ПОВОРАЧИВАЕТСЯ. В рое полторы сотни хлопьев, и любой
 * его поворот виден как поворот всей россыпи разом — жесткой пластинкой
 * (замечание владельца игры: так выглядели рои, которые закручивало вихрями
 * за горами). Поэтому вихри роев не касаются, и сам по себе рой тоже не
 * кружится: как лег при рождении заряда, так и летит. Кружат только отдельные
 * снежинки.
 *
 * ЗАВИХРЕНИЯ. Вихрь — небольшой круг (радиусом в ячейку-полторы), в котором
 * снег идет по кругу. Вихри бывают двух родов:
 *  - за горой, с подветренной стороны, — два вихря, закрученные навстречу друг
 *    другу, как за камнем в потоке: между ними снег тянет назад, к горе, по
 *    внешним их краям — вперед по ветру. Стоят на месте. Спрайтов на все горы
 *    не хватит: вихри достаются нескольким — сначала тем, что в кадре камеры в
 *    миг рождения заряда, остальные раскиданы по всему полю (заряд идет
 *    полминуты, камеру успеют передвинуть);
 *  - вольные: несколько вихрей медленно плывут по ветру над всей областью
 *    заряда — завихрения видны и там, где гор нет.
 * Через вихрь идет СТРУЯ (через вольный — две): семь-восемь снежинок летят
 * друг за другом по одному и тому же пути. Снежинка приходит с ветром по своей
 * дорожке, входит в вихрь с той его стороны, где он крутит по ветру, делает
 * вокруг его середины виток с лишним и уходит дальше по той же дорожке; до
 * вихря она проступает, после — тает и возвращается в начало пути. Глаз видит
 * не комок, а вереницу отдельных снежинок, идущую по кругу.
 *
 * ПУТЬ СНЕЖИНКИ В ВИХРЕ — ее прямая дорожка, повернутая вокруг середины вихря
 * на угол, накопленный за пройденную часть вихря (доля полного поворота —
 * пройденная по дорожке доля силы вихря; сила — единица в середине и сходит на
 * нет к кромке). Полный поворот — целое число оборотов: на выходе снежинка
 * снова на своей дорожке и летит по ветру, без излома. От середины вихря она
 * при этом на том же расстоянии, что и на прямой дорожке: чем ближе дорожка к
 * середине, тем туже виток. Угол — не от времени, а от места на дорожке, и
 * путь остается функцией времени.
 *
 * СКОРОСТЬ. Вне вихря снежинка летит со скоростью потока. В вихре — не
 * быстрее, а к его середине и медленнее (за горой затишье, снег там кружит не
 * спеша): виток втрое длиннее прямой дорожки через вихрь, и пройди снежинка
 * его за то же время, она неслась бы втрое быстрее остального снега (владелец
 * игры: быстрый снег — уже помехи). Поэтому путь расписан по времени заранее,
 * при рождении заряда: в какой миг снежинка в каком месте дорожки (таблица,
 * см. buildTimetable).
 *
 * СНЕГ ПОКРЫВАЕТ ОБЛАСТЬ РОВНО, без пятен и прорех: дорожки роев поперек
 * ветра идут через равные промежутки, места на круге вдоль ветра разложены
 * золотым сечением, а скорости роев одного слоя почти равны — раскладка не
 * сбивается в комки.
 *
 * Основная масса снега, мгла и пелена идут над строениями и горами, но под
 * отрядами (слой LOW_AIR): отряды и их значки в бурю читаются. Поверх отрядов
 * (слой AIR) летит только редкий бледный снег — немного роев и по снежинке
 * из каждой струи завихрений (каждая пятая по счету).
 *
 * Область заряда — все поле с запасом вокруг, куда бы ни смотрела камера:
 * заряд идет полминуты, и игрок, передвинув камеру, не должен найти там ясную
 * погоду. Только на огромном поле, где роев не хватило бы и на треть заданной
 * густоты, заряд идет в окне вокруг камеры.
 */

const FLAKES = 'flakes';
const HAZE = 'haze';
const VEIL = 'veil';
const SNOWFLAKE = 'snowflake';

/** Метка дорожки вьюги: по ней сцена находит идущий заряд и решает, можно ли ему начаться */
export const SNOW_SQUALL_TAG = 'snowSquall';

// Золотое сечение: места роев на круге пути раскладываются им равномерно
const GOLDEN_SHARE = (Math.sqrt(5) - 1) / 2;

/**
 * Какую долю кадра роя занимают снежинки в полную силу (у кромки кадра они
 * бледнеют, см. fieldBiomeTextures: FLURRY) и какую долю кадра клуба мглы —
 * его плотная середина
 */
export const SHEET_INK_SHARE = 0.62;
export const HAZE_CORE_SHARE = 0.28;

/**
 * Расписание пути снежинки через вихрь — таблица «время → место на дорожке» —
 * считается при рождении заряда: на столько отрезков делится дорожка при счете
 * времени и на столько равных отрезков времени — готовая таблица. Между узлами
 * таблицы место берется по прямой: при этих числах скорость снежинки гуляет
 * вокруг заданной на считаные проценты
 */
const TIMETABLE_PATH_STEPS = 512;
const TIMETABLE_STEPS = 320;

const resolveMiddle = ({min, max}) => (min + max) / 2;
/** Сколько частиц нужно, чтобы покрыть площадь заданное число раз (без потолка) */
const resolveWantedCount = (coverage, area, meanSize, inkShare) => (coverage * area) / (meanSize * meanSize * inkShare);

export const SNOW_SQUALL_TEXTURES = [
  FIELD_BIOME_TEXTURES.FLURRY, FIELD_BIOME_TEXTURES.PUFF, FIELD_BIOME_TEXTURES.SNOWFLAKE,
];

/**
 * Сила заряда в момент времени: 0 — его нет, 1 — в разгаре. Плавно нарастает
 * в начале и плавно спадает в конце — так идут и картинка, и звук вьюги.
 *
 * @param {{durationMs: number, riseShare: number, fallShare: number}} group — группа заряда
 * @param {number} elapsedMs
 * @returns {number} 0..1
 */
export const resolveSnowSquallStrength = (group, elapsedMs) => (
  resolveLifeEnvelope(elapsedMs / group.durationMs, group.riseShare, group.fallShare)
);

/**
 * Скорость потока снега в заряде, точек мира в секунду: с такой скоростью в
 * разгар вьюги несутся и облака антуража (см. scenes/FieldAmbience).
 *
 * @param {{streamSpeed: number}} group — группа заряда
 * @returns {number}
 */
export const resolveSnowSquallStreamSpeed = (group) => group.streamSpeed;

/**
 * Какая доля полного поворота накоплена к этому месту дорожки через вихрь:
 * place — от -1 (вход в вихрь) до 1 (выход). Это пройденная по дорожке доля
 * силы вихря (сила — 1 − d²/R², d — расстояние до середины: единица в середине,
 * ноль на кромке): многочлен, без памяти о прошлом. У входа и у выхода поворот
 * не копится вовсе — снежинка входит в вихрь и выходит из него без излома
 */
const resolveTurnShare = (place) => (3 * place - place ** 3 + 2) / 4;
/** Как быстро копится поворот в этом месте дорожки: производная resolveTurnShare */
const resolveTurnRate = (place) => (3 / 4) * (1 - place * place);

/**
 * Дорожки вьюги.
 *
 * @param {Object} context — из createBiomeContext
 * @param {Object} params — числа вьюги (`squall` из чисел зимней подложки)
 * @param {number} tint — краска снега и мглы
 * @returns {Array} дорожки для контроллера эффекта
 */
export const createSnowSquallTracks = (context, params, tint) => {
  const {hexSize, wind} = context;
  const across = {x: -wind.y, y: wind.x};
  const meanSizeOf = ({sizeFactor}) => hexSize * resolveMiddle(sizeFactor);

  /** Область заряда по ее рамке: середина и полуразмеры вдоль ветра и поперек него */
  const describeArea = (box) => {
    const halfWidth = (box.right - box.left) / 2;
    const halfHeight = (box.bottom - box.top) / 2;
    return {
      centre: {x: (box.left + box.right) / 2, y: (box.top + box.bottom) / 2},
      reachAlong: Math.abs(wind.x) * halfWidth + Math.abs(wind.y) * halfHeight,
      reachAcross: Math.abs(across.x) * halfWidth + Math.abs(across.y) * halfHeight,
    };
  };
  /** Площадь, которую частицы такого размера проходят за круг: полоса вдоль ветра длиной в круг */
  const resolveLapArea = (area, size) => (2 * area.reachAlong + size) * 2 * area.reachAcross;
  /** Место точки мира в осях заряда: вдоль ветра и поперек него от середины области */
  const resolvePlace = (area, {x, y}) => ({
    along: (x - area.centre.x) * wind.x + (y - area.centre.y) * wind.y,
    lateral: (x - area.centre.x) * across.x + (y - area.centre.y) * across.y,
  });

  // Поле с запасом, в пределах картинки пейзажа
  const fieldBox = intersectBoxes(expandBox(context.fieldBox, hexSize * params.fieldMarginFactor), context.rect)
    || context.rect;
  const fieldArea = describeArea(fieldBox);
  const flakesSize = meanSizeOf(params.flakes);
  // На огромном поле роев не хватило бы и на треть заданной густоты: там
  // заряд идет в окне вокруг камеры
  const isFieldTooLarge = resolveWantedCount(
    params.flakes.coverage, resolveLapArea(fieldArea, flakesSize), flakesSize, SHEET_INK_SHARE
  ) > params.flakes.maxCount * params.narrowWhenOver;

  /** Область заряда: все поле; на огромном поле — окно вокруг того, что видит камера */
  const resolveSquallArea = (view) => {
    if (!isFieldTooLarge || !view) {
      return fieldArea;
    }
    const window = intersectBoxes(expandBox(view, hexSize * params.viewMarginFactor), fieldBox);
    return window ? describeArea(window) : fieldArea;
  };

  /** Сколько частиц покроет круг пути заданное число раз, но не больше потолка */
  const resolveCount = (numbers, area, inkShare) => {
    const size = meanSizeOf(numbers);
    const wanted = resolveWantedCount(numbers.coverage, resolveLapArea(area, size), size, inkShare);
    return Math.max(1, Math.min(numbers.maxCount, Math.round(wanted)));
  };

  /** Рои одного слоя: ровная раскладка по кругу пути */
  const buildFlakes = (rng, layer, numbers, area) => {
    const count = resolveCount(numbers, area, SHEET_INK_SHARE);
    const lapOffset = rng();
    const flakes = [];
    for (let index = 0; index < count; index += 1) {
      // Дорожки роев поперек ветра — через равные промежутки, со своим сдвигом
      const lane = ((index + rng()) / count - 0.5) * 2 * area.reachAcross;
      flakes.push({
        kind: FLAKES,
        layer,
        texture: FIELD_BIOME_TEXTURES.FLURRY,
        tint,
        size: hexSize * randomIn(rng, numbers.sizeFactor),
        lane,
        // Где рой на своем круге в начале заряда (доля круга): золотое
        // сечение разводит соседние дорожки и вдоль ветра
        lapShare: (lapOffset + index * GOLDEN_SHARE) % 1,
        speed: hexSize * randomIn(rng, numbers.speedFactor),
        alpha: randomIn(rng, numbers.alpha),
        // Снежинки круглые: рой лежит как придется — и не поворачивается
        rotation: rng() * TWO_PI,
        sway: hexSize * randomIn(rng, numbers.swayFactor),
        swayPeriodMs: randomIn(rng, numbers.swayPeriodMs),
        swayPhase: rng() * TWO_PI,
      });
    }
    return flakes;
  };

  // --- завихрения: струи отдельных снежинок

  const {eddies} = params;

  /**
   * Расписание пути струи: в какой миг снежинка в каком месте своей дорожки
   * (место — вдоль ветра от середины вихря, от -reach до reach). Вне вихря она
   * летит со скоростью speed, в вихре — с нею же вдоль витка, а к середине
   * вихря медленнее.
   *
   * @returns {{pathMs: number, vortexMs: number, places: number[]}} за сколько
   *   снежинка проходит весь путь и сколько из этого — сам вихрь (его оборот);
   *   ее места через равные промежутки времени пути
   */
  const buildTimetable = ({reach, lane, chord, turnRad}, speed) => {
    /** Секунд на единицу дорожки в этом ее месте */
    const resolvePace = (place) => {
      if (Math.abs(place) >= chord) {
        return 1 / speed;
      }
      const share = place / chord;
      // Виток длиннее дорожки: к шагу вдоль нее добавляется поворот вокруг середины вихря
      const turnPerStep = (turnRad * resolveTurnRate(share)) / chord;
      const stretch = Math.hypot(1 - turnPerStep * lane, turnPerStep * place);
      // В середине вихря затишье: скорость вдоль витка — доля скорости потока
      const calm = 1 - (1 - eddies.stream.coreSpeedShare) * (1 - share * share) ** 2;
      return stretch / (speed * calm);
    };
    // Время от начала дорожки до каждого ее узла (правило Симпсона на каждом отрезке)
    const step = (2 * reach) / TIMETABLE_PATH_STEPS;
    const times = [0];
    for (let index = 0; index < TIMETABLE_PATH_STEPS; index += 1) {
      const place = -reach + index * step;
      times.push(times[index] + (step / 6) * (
        resolvePace(place) + 4 * resolvePace(place + step / 2) + resolvePace(place + step)
      ));
    }
    const total = times[TIMETABLE_PATH_STEPS];
    // То же наоборот: места через равные промежутки времени
    const places = [-reach];
    let node = 0;
    for (let index = 1; index < TIMETABLE_STEPS; index += 1) {
      const time = (total * index) / TIMETABLE_STEPS;
      while (node < TIMETABLE_PATH_STEPS - 1 && times[node + 1] < time) {
        node += 1;
      }
      places.push(-reach + (node + (time - times[node]) / (times[node + 1] - times[node])) * step);
    }
    places.push(reach);
    // Сколько из этого снежинка идет через сам вихрь: до него и после него она летит прямо, со скоростью speed
    const vortexMs = (total - (2 * (reach - chord)) / speed) * 1000;
    return {pathMs: total * 1000, vortexMs, places};
  };

  /**
   * Струя через вихрь: сам вихрь и путь снежинок через него.
   *
   * @param {Object} vortex — {along, lateral, radius, sense, driftSpeed}:
   *   середина вихря в осях заряда, его радиус, в какую сторону он крутит (1 —
   *   от ветра к оси «поперек», -1 — обратно) и как быстро плывет по ветру
   *   (0 — стоит)
   * @param {number} laneShare — на каком расстоянии от середины вихря идет
   *   дорожка струи, доля его радиуса
   * @returns {Object} описание струи: одно на все ее снежинки
   */
  const buildStream = (rng, area, vortex, laneShare) => {
    const numbers = eddies.stream;
    const {radius, sense} = vortex;
    // Дорожка идет мимо середины вихря с той стороны, где он крутит по ветру:
    // снежинка и на прямом пути обходит середину в ту же сторону — вихрь ее
    // только доворачивает, вспять она не идет
    const lane = -sense * radius * laneShare;
    // Полухорда вихря на дорожке: снежинка входит в вихрь в -chord и выходит в +chord
    const chord = Math.sqrt(radius * radius - lane * lane);
    // До вихря и после него — по отрезку пути, на котором снежинка проступает и тает
    const reach = chord + hexSize * numbers.legFactor;
    // Круг вихря через область: вольный вихрь уходит за ее подветренный край и
    // появляется с наветренного, как рои; круг длиннее области на длину пути
    // струи — у краев она целиком за областью. Вихрь за горой стоит на своем
    // месте этого круга
    const vortexLap = 2 * (area.reachAlong + reach);
    const path = {
      radius,
      sense,
      lane,
      chord,
      reach,
      // Полный поворот снежинки в вихре — целое число оборотов
      turnRad: sense * TWO_PI * numbers.turns,
    };
    const speed = hexSize * randomIn(rng, params.flakes.speedFactor);
    return {
      ...path,
      lateral: vortex.lateral,
      vortexLap,
      // Где вихрь на своем круге в начале заряда (доля круга)
      vortexLapShare: (vortex.along + vortexLap / 2) / vortexLap,
      driftSpeed: vortex.driftSpeed,
      // Вихрь за горой виден вспышками: номер расписания своей горы (group.bursts); у вольного вихря его нет
      ...(vortex.burst === undefined ? {} : {burst: vortex.burst}),
      // Мимо вихря снежинка летит со скоростью потока, а вихрь плывет следом:
      // через него она идет с их разницей
      ...buildTimetable(path, speed - vortex.driftSpeed),
    };
  };

  /**
   * Снежинки струи.
   *
   * @param {number} stream — номер струи в заряде (group.streams)
   * @param {number} flakeCount — сколько в струе снежинок
   */
  const buildSnowflakes = (rng, stream, flakeCount) => {
    const {stream: numbers, flake} = eddies;
    const phase = rng();
    const flakes = [];
    for (let index = 0; index < flakeCount; index += 1) {
      // Каждая пятая — поверх отрядов, бледнее остальных
      const isAboveUnits = index % numbers.aboveUnitsEvery === numbers.aboveUnitsEvery - 1;
      const size = hexSize * randomIn(rng, flake.sizeFactor);
      flakes.push({
        kind: SNOWFLAKE,
        layer: isAboveUnits ? FIELD_BIOME_LAYER_KEYS.AIR : FIELD_BIOME_LAYER_KEYS.LOW_AIR,
        // Над роями своего слоя, на своей глубине: спрайты одной текстуры идут подряд
        sublayer: isAboveUnits ? 1 : 2,
        texture: FIELD_BIOME_TEXTURES.SNOWFLAKE,
        tint,
        // Хлопок чуть сплюснут и лежит как придется — как хлопья в рое
        width: size * randomIn(rng, flake.stretch),
        height: size,
        rotation: rng() * TWO_PI,
        alpha: randomIn(rng, flake.alpha) * (isAboveUnits ? flake.aboveUnitsAlphaShare : 1),
        stream,
        // Где снежинка на пути струи в начале заряда (доля времени пути):
        // снежинки идут друг за другом через почти равные промежутки
        pathShare: (phase + (index + randomIn(rng, numbers.gapJitter)) / flakeCount + 1) % 1,
        // И не по нитке: каждая чуть в стороне от общего пути
        shiftAlong: hexSize * randomIn(rng, numbers.scatterFactor),
        shiftLateral: hexSize * randomIn(rng, numbers.scatterFactor),
      });
    }
    return flakes;
  };

  /**
   * Полоса области поперек ветра, которую видит камера: {from, to} в осях
   * заряда; null — камеры нет или она смотрит мимо области
   */
  const resolveViewBand = (area, view) => {
    if (!view) {
      return null;
    }
    const middle = resolvePlace(area, {x: (view.left + view.right) / 2, y: (view.top + view.bottom) / 2});
    const width = view.right - view.left;
    const height = view.bottom - view.top;
    const reachAlong = (Math.abs(wind.x) * width + Math.abs(wind.y) * height) / 2;
    const reachAcross = (Math.abs(across.x) * width + Math.abs(across.y) * height) / 2;
    const from = Math.max(-area.reachAcross, middle.lateral - reachAcross);
    const to = Math.min(area.reachAcross, middle.lateral + reachAcross);
    return to > from && Math.abs(middle.along) < area.reachAlong + reachAlong ? {from, to} : null;
  };

  /**
   * Вольные вихри: плывут по ветру над всей областью заряда. Часть — в полосе,
   * которую видит камера, остальные — по всей ширине области; вдоль ветра
   * разведены золотым сечением
   */
  const buildFreeVortices = (rng, area, view) => {
    const {count, nearShare, driftShare} = eddies.free;
    const band = resolveViewBand(area, view);
    const nearCount = band ? Math.round(count * nearShare) : 0;
    const lapOffset = rng();
    const vortices = [];
    for (let index = 0; index < count; index += 1) {
      const isNear = index < nearCount;
      const from = isNear ? band.from : -area.reachAcross;
      const to = isNear ? band.to : area.reachAcross;
      // Дорожки вихрей поперек ветра — через равные промежутки своей полосы
      const slot = isNear ? index : index - nearCount;
      const slotCount = isNear ? nearCount : count - nearCount;
      vortices.push({
        along: (((lapOffset + index * GOLDEN_SHARE) % 1) - 0.5) * 2 * area.reachAlong,
        lateral: from + ((slot + rng()) / slotCount) * (to - from),
        sense: randomSign(rng),
        // Вихрь плывет по ветру медленнее потока: снег идет сквозь него
        driftSpeed: hexSize * resolveMiddle(params.flakes.speedFactor) * randomIn(rng, driftShare),
      });
    }
    return vortices;
  };

  /**
   * Горы, за которыми в этом заряде стоят вихри. Сначала — горы в кадре
   * камеры (в случайном порядке: от заряда к заряду вихри переходят от горы
   * к горе), потом — по всему полю: каждая следующая — самая дальняя от уже
   * выбранных. Соседние горы гряды вихрей разом не получают: их вихри легли
   * бы друг на друга
   */
  const pickMountains = (rng, area, view) => {
    const {maxMountains, nearShare, gapFactor, viewMarginFactor, leeFactor} = eddies.lee;
    // Горы, вихри за которыми стоят в области заряда
    const candidates = [];
    context.mountains.forEach((mountain) => {
      const place = resolvePlace(area, mountain);
      const isInArea = Math.abs(place.along + hexSize * leeFactor) < area.reachAlong
        && Math.abs(place.lateral) < area.reachAcross;
      if (isInArea) {
        candidates.push({...place, x: mountain.x, y: mountain.y});
      }
    });
    const chosen = [];
    const resolveGap = (candidate) => chosen.reduce((gap, other) => (
      Math.min(gap, Math.hypot(candidate.along - other.along, candidate.lateral - other.lateral))
    ), Infinity);
    const minGap = hexSize * gapFactor;

    if (view) {
      const seen = expandBox(view, hexSize * viewMarginFactor);
      const near = candidates.filter(({x, y}) => x > seen.left && x < seen.right && y > seen.top && y < seen.bottom);
      const nearCount = Math.round(maxMountains * nearShare);
      // Случайный порядок: перестановка Фишера — Йетса
      for (let index = near.length - 1; index > 0; index -= 1) {
        const other = Math.min(index, Math.floor(rng() * (index + 1)));
        [near[index], near[other]] = [near[other], near[index]];
      }
      for (let index = 0; index < near.length && chosen.length < nearCount; index += 1) {
        if (resolveGap(near[index]) >= minGap) {
          chosen.push(near[index]);
        }
      }
    }
    // В кадре гор нет (или нет камеры): первая — любая
    if (chosen.length === 0 && candidates.length > 0 && maxMountains > 0) {
      chosen.push(candidates[Math.min(candidates.length - 1, Math.floor(rng() * candidates.length))]);
    }
    while (chosen.length < maxMountains) {
      let farthest = null;
      let farthestGap = minGap;
      for (let index = 0; index < candidates.length; index += 1) {
        const gap = resolveGap(candidates[index]);
        if (gap >= farthestGap && (gap > farthestGap || !farthest)) {
          farthest = candidates[index];
          farthestGap = gap;
        }
      }
      if (!farthest) {
        break;
      }
      chosen.push(farthest);
    }
    return chosen;
  };

  /** Вихри за горами: по два на гору, с подветренной стороны, закручены навстречу */
  const buildLeeVortices = (rng, area, view) => {
    const {leeFactor, sideFactor} = eddies.lee;
    const vortices = [];
    pickMountains(rng, area, view).forEach(({along, lateral}, mountainIndex) => {
      [-1, 1].forEach((side) => {
        vortices.push({
          along: along + hexSize * leeFactor,
          lateral: lateral + side * hexSize * sideFactor,
          // Как вихри за камнем в потоке: вихрь по одну руку от ветра крутит в
          // свою сторону, и между вихрями поток идет вспять
          sense: -side,
          driftSpeed: 0,
          // Оба вихря горы вспыхивают вместе: расписание у них одно
          ...(eddies.lee.burst ? {burst: mountainIndex} : {}),
        });
      });
    });
    return vortices;
  };

  /**
   * Расписания вспышек вихрей за горами — по одному на гору. Считаются после
   * всех струй: ни одного прежнего числа заряда они не меняют.
   *
   * @returns {Array<{seed, cycleMs, phaseMs, turnMs}>} seed — зерно, по
   *   которому у каждой вспышки свои длина и миг начала; cycleMs — срок, за
   *   который у горы одна вспышка; phaseMs — сдвиг этих сроков; turnMs — оборот
   *   вихря (средний по струям горы)
   */
  const buildBursts = (rng, streams) => {
    const turns = [];
    streams.forEach(({burst, vortexMs}) => {
      if (burst !== undefined) {
        turns[burst] = [...(turns[burst] || []), vortexMs];
      }
    });
    return turns.map((vortexTimes) => {
      const cycleMs = randomIn(rng, eddies.lee.burst.cycleMs);
      return {
        seed: 1 + rng() * 1000,
        cycleMs,
        phaseMs: rng() * cycleMs,
        turnMs: vortexTimes.reduce((sum, value) => sum + value, 0) / vortexTimes.length,
      };
    });
  };

  /**
   * Насколько вихрь за горой сейчас виден: 0 — пауза, 1 — вспышка в разгаре.
   * Сколько оборотов длится вспышка и когда она начинается в своем сроке —
   * по номеру срока: поза остается функцией времени
   */
  const resolveBurstEnvelope = ({seed, cycleMs, phaseMs, turnMs}, elapsedMs) => {
    const {turns, fadeInShare, fadeOutShare, maxCycleShare} = eddies.lee.burst;
    const since = (elapsedMs + phaseMs) / cycleMs;
    const cycle = Math.floor(since);
    const activeMs = Math.min(lerp(turns.min, turns.max, hash01(seed, cycle)) * turnMs, cycleMs * maxCycleShare);
    // Между вспышками — пауза: по половине ее в начале и в конце каждого срока вспышек нет
    const pauseMs = cycleMs * (1 - maxCycleShare);
    const startMs = pauseMs / 2 + hash01(seed + 0.5, cycle) * (cycleMs - activeMs - pauseMs);
    return resolveLifeEnvelope(((since - cycle) * cycleMs - startMs) / activeMs, fadeInShare, fadeOutShare);
  };

  const buildGroup = ({rng, view}) => {
    const area = resolveSquallArea(view);
    const particles = [];
    // Пелена и мгла — под снегом своего слоя
    particles.push({
      kind: VEIL,
      layer: FIELD_BIOME_LAYER_KEYS.LOW_AIR,
      sublayer: 0,
      texture: FIELD_BIOME_TEXTURES.PUFF,
      tint,
      // Плотная середина пятна накрывает всю область
      size: 2 * Math.max(area.reachAlong, area.reachAcross) * params.veil.sizeShare,
      alpha: params.veil.alpha,
      rotation: rng() * TWO_PI,
    });
    const hazeCount = resolveCount(params.haze, area, HAZE_CORE_SHARE);
    const hazeLapOffset = rng();
    for (let index = 0; index < hazeCount; index += 1) {
      particles.push({
        kind: HAZE,
        layer: FIELD_BIOME_LAYER_KEYS.LOW_AIR,
        sublayer: 0,
        texture: FIELD_BIOME_TEXTURES.PUFF,
        tint,
        size: hexSize * randomIn(rng, params.haze.sizeFactor),
        lane: ((index + rng()) / hazeCount - 0.5) * 2 * area.reachAcross,
        lapShare: (hazeLapOffset + index * GOLDEN_SHARE) % 1,
        speed: hexSize * randomIn(rng, params.haze.speedFactor),
        alpha: randomIn(rng, params.haze.alpha),
        rotation: rng() * TWO_PI,
        spinRadPerMs: (toRadians(params.haze.spinDegPerSecond) / 1000) * randomSign(rng),
        sway: 0,
        swayPeriodMs: 1,
        swayPhase: 0,
      });
    }
    buildFlakes(rng, FIELD_BIOME_LAYER_KEYS.LOW_AIR, params.flakes, area)
      .forEach((flakes) => particles.push({...flakes, sublayer: 1}));
    buildFlakes(rng, FIELD_BIOME_LAYER_KEYS.AIR, params.top, area)
      .forEach((flakes) => particles.push(flakes));

    const group = {
      durationMs: randomIn(rng, params.durationMs),
      riseShare: params.riseShare,
      fallShare: params.fallShare,
      centre: area.centre,
      reachAlong: area.reachAlong,
      // Скорость потока снега — середина скоростей роев основной массы
      streamSpeed: hexSize * resolveMiddle(params.flakes.speedFactor),
      wavePeriodMs: randomIn(rng, params.wavePeriodMs),
      wavePhase: rng() * TWO_PI,
      // Струи завихрений: вихрь и путь через него. У снежинки — номер ее струи
      streams: [],
      // Расписания вспышек вихрей за горами: по одному на гору (см. buildBursts)
      bursts: [],
      particles,
    };
    // Завихрения — последними: ни камера, ни горы не меняют остального заряда
    // (источник случайности до этого места тратится одинаково), а горы — еще и
    // вольных вихрей
    [[buildFreeVortices, eddies.free], [buildLeeVortices, eddies.lee]].forEach(([buildVortices, numbers]) => {
      buildVortices(rng, area, view).forEach((vortex) => {
        const radius = hexSize * randomIn(rng, eddies.radiusFactor);
        const {min, max} = numbers.laneShare;
        for (let index = 0; index < numbers.streamCount; index += 1) {
          // Дорожки струй одного вихря — на разных расстояниях от его середины: у каждой свой промежуток
          const laneShare = min + ((index + rng()) / numbers.streamCount) * (max - min);
          group.streams.push(buildStream(rng, area, {...vortex, radius}, laneShare));
          particles.push(...buildSnowflakes(rng, group.streams.length - 1, numbers.flakeCount));
        }
      });
    });
    // Вспышки — самыми последними: расписание вспышек не меняет ни одного прежнего числа заряда
    group.bursts = buildBursts(rng, group.streams);
    return group;
  };

  /** Поза снежинки завихрения; density — сила заряда с его волной */
  const resolveSnowflakePose = (group, particle, elapsedMs, density, out) => {
    const stream = group.streams[particle.stream];
    // Вихрь за горой виден вспышками: между ними его снежинок нет
    const burst = stream.burst === undefined ? 1 : resolveBurstEnvelope(group.bursts[stream.burst], elapsedMs);
    if (!(burst > 0)) {
      return writeHiddenPose(out);
    }
    // Путь струи замкнут по кругу: растаяв за вихрем, снежинка возвращается в его начало
    const passed = particle.pathShare + elapsedMs / stream.pathMs;
    const cell = (passed - Math.floor(passed)) * TIMETABLE_STEPS;
    const node = Math.min(TIMETABLE_STEPS - 1, Math.floor(cell));
    // Место на дорожке — по расписанию пути
    const place = stream.places[node] + (stream.places[node + 1] - stream.places[node]) * (cell - node);
    // До вихря снежинка проступает, после него — тает
    const fade = Math.min(
      smoothstep(-stream.reach, -stream.chord, place), smoothstep(stream.reach, stream.chord, place)
    );
    // Середина вихря на его круге через область: вольный вихрь плывет по ветру,
    // вихрь за горой стоит. К краям области завихрение сходит на нет, как и рои
    const travelled = stream.vortexLapShare * stream.vortexLap + (stream.driftSpeed * elapsedMs) / 1000;
    const lap = travelled - Math.floor(travelled / stream.vortexLap) * stream.vortexLap;
    const edge = Math.max(0, Math.sin((Math.PI * lap) / stream.vortexLap)) ** params.edgePower;
    let along = place;
    let lateral = stream.lane;
    if (Math.abs(place) < stream.chord) {
      // В вихре дорожка повернута вокруг его середины на угол, накопленный за
      // пройденную часть вихря: у каждой снежинки он свой — по ее месту
      const angle = stream.turnRad * resolveTurnShare(place / stream.chord);
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      along = cos * place - sin * stream.lane;
      lateral = sin * place + cos * stream.lane;
    }
    along += lap - stream.vortexLap / 2 + particle.shiftAlong;
    lateral += stream.lateral + particle.shiftLateral;
    return writePose(
      out,
      group.centre.x + wind.x * along + across.x * lateral,
      group.centre.y + wind.y * along + across.y * lateral,
      particle.alpha * density * fade * edge * burst,
      particle.width, particle.height,
      particle.rotation
    );
  };

  const resolvePose = (group, particle, elapsedMs, out) => {
    // Заряд плавно налетает, держится и плавно стихает
    const strength = resolveSnowSquallStrength(group, elapsedMs);
    if (!(strength > 0)) {
      return writeHiddenPose(out);
    }
    if (particle.kind === VEIL) {
      // Пелена стоит над областью заряда: волны ее не колышут
      return writePose(
        out, group.centre.x, group.centre.y, particle.alpha * strength, particle.size, particle.size, particle.rotation
      );
    }
    // …и идет волнами
    const wave = 1 - params.waveDepth
      * (0.5 + 0.5 * Math.sin((TWO_PI * elapsedMs) / group.wavePeriodMs + group.wavePhase));
    if (particle.kind === SNOWFLAKE) {
      return resolveSnowflakePose(group, particle, elapsedMs, strength * wave, out);
    }

    // Путь через область замкнут по кругу: ушел за подветренный край —
    // появился с наветренного. Круг длиннее области на размер частицы: у
    // краев она целиком за областью
    const lapLength = 2 * group.reachAlong + particle.size;
    const travelled = particle.lapShare * lapLength + (particle.speed * elapsedMs) / 1000;
    const lap = travelled - Math.floor(travelled / lapLength) * lapLength;
    // К краям области частица сходит на нет: ее уход и появление не видны.
    // У самого шва круга округление может дать синус чуть ниже нуля — степень
    // от него была бы не числом
    const edge = Math.max(0, Math.sin((Math.PI * lap) / lapLength)) ** params.edgePower;
    const along = lap - lapLength / 2;
    const lateral = particle.lane
      + particle.sway * Math.sin((TWO_PI * elapsedMs) / particle.swayPeriodMs + particle.swayPhase);
    return writePose(
      out,
      group.centre.x + wind.x * along + across.x * lateral,
      group.centre.y + wind.y * along + across.y * lateral,
      particle.alpha * strength * wave * edge,
      particle.size, particle.size,
      // Кружится только клуб мглы: он — одно мягкое пятно. Рой — россыпь хлопьев, и его поворот виден как
      // поворот всей россыпи разом
      particle.kind === HAZE ? particle.rotation + particle.spinRadPerMs * elapsedMs : particle.rotation
    );
  };

  return [{
    tag: SNOW_SQUALL_TAG,
    schedule: {firstDelayMs: params.firstDelayMs, intervalMs: params.intervalMs, maxGroups: 1},
    buildGroup,
    resolvePose,
  }];
};
